"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActorDereferenceHttpBase = void 0;
exports.mediaTypesToAcceptString = mediaTypesToAcceptString;
const bus_dereference_1 = require("@comunica/bus-dereference");
const bus_http_1 = require("@comunica/bus-http");
const core_1 = require("@comunica/core");
const stream_to_string_1 = require("@jeswr/stream-to-string");
const relative_to_absolute_iri_1 = require("relative-to-absolute-iri");
const DereferenceCachePolicyHttpWrapper_1 = require("./DereferenceCachePolicyHttpWrapper");
const REGEX_MEDIATYPE = /^[^ ;]*/u;
const REGEX_VERSION_HEADER = /version=([^ ;]*)/u;
function mediaTypesToAcceptString(mediaTypes, maxLength) {
    const wildcard = '*/*;q=0.1';
    const parts = [];
    const sortedMediaTypes = Object.entries(mediaTypes)
        .map(([mediaType, priority]) => ({ mediaType, priority }))
        .sort((left, right) => right.priority === left.priority ?
        left.mediaType.localeCompare(right.mediaType) :
        right.priority - left.priority);
    // Take into account the ',' characters joining each type
    let partsLength = sortedMediaTypes.length - 1;
    for (const { mediaType, priority } of sortedMediaTypes) {
        const part = mediaType + (priority === 1 ? '' : `;q=${priority.toFixed(3).replace(/0*$/u, '')}`);
        if (partsLength + part.length > maxLength) {
            while (partsLength + wildcard.length > maxLength) {
                const last = parts.pop() ?? '';
                // Don't forget the ','
                partsLength -= last.length + 1;
            }
            parts.push(wildcard);
            break;
        }
        parts.push(part);
        partsLength += part.length;
    }
    return parts.length === 0 ? '*/*' : parts.join(',');
}
/**
 * An actor that listens on the 'dereference' bus.
 *
 * It resolves the URL using the HTTP bus using an accept header compiled from the available media types.
 */
class ActorDereferenceHttpBase extends bus_dereference_1.ActorDereference {
    mediatorHttp;
    maxAcceptHeaderLength;
    maxAcceptHeaderLengthBrowser;
    constructor(args) {
        super(args);
        this.mediatorHttp = args.mediatorHttp;
        this.maxAcceptHeaderLength = args.maxAcceptHeaderLength;
        this.maxAcceptHeaderLengthBrowser = args.maxAcceptHeaderLengthBrowser;
    }
    async test({ url }) {
        if (!/^https?:/u.test(url)) {
            return (0, core_1.failTest)(`Cannot retrieve ${url} because it is not an HTTP(S) URL.`);
        }
        return (0, core_1.passTestVoid)();
    }
    async run(action) {
        let exists = true;
        const maxAcceptHeaderLength = this.getMaxAcceptHeaderLength();
        const headers = await ActorDereferenceHttpBase.establishAcceptHeader(action, maxAcceptHeaderLength);
        let httpResponse;
        const requestTimeStart = Date.now();
        try {
            httpResponse = await this.mediatorHttp.mediate({
                context: action.context,
                init: { headers, method: action.method },
                input: action.url,
            });
        }
        catch (error) {
            return this.handleDereferenceErrors(action, error);
        }
        // The response URL can be relative to the given URL
        const url = (0, relative_to_absolute_iri_1.resolve)(httpResponse.url, action.url);
        const requestTime = Date.now() - requestTimeStart;
        // Only parse if retrieval was successful
        if (httpResponse.status !== 200) {
            exists = false;
            // Consume the body, to avoid process to hang
            const bodyString = httpResponse.body ?
                await (0, stream_to_string_1.stringify)(bus_http_1.ActorHttp.toNodeReadable(httpResponse.body)) :
                'empty response';
            if (!action.acceptErrors) {
                const error = new Error(`Could not retrieve ${action.url} (HTTP status ${httpResponse.status}):\n${bodyString}`);
                return this.handleDereferenceErrors(action, error, httpResponse.headers, requestTime);
            }
        }
        const contentType = httpResponse.headers.get('content-type') ?? '';
        const mediaType = REGEX_MEDIATYPE.exec(contentType)?.[0];
        const version = REGEX_VERSION_HEADER.exec(contentType)?.[1];
        // Return the parsed quad stream and whether or not only triples are supported
        return {
            url,
            data: exists ? bus_http_1.ActorHttp.toNodeReadable(httpResponse.body) : (0, bus_dereference_1.emptyReadable)(),
            exists,
            requestTime,
            status: httpResponse.status,
            headers: httpResponse.headers,
            mediaType: mediaType === 'text/plain' || mediaType === 'application/octet-stream' ? undefined : mediaType,
            version,
            cachePolicy: httpResponse.cachePolicy ?
                new DereferenceCachePolicyHttpWrapper_1.DereferenceCachePolicyHttpWrapper(httpResponse.cachePolicy, maxAcceptHeaderLength) :
                undefined,
        };
    }
    static async establishAcceptHeader(action, maxAcceptHeaderLength) {
        // Append any custom passed headers
        const headers = new Headers(action.headers);
        // Resolve HTTP URL using appropriate accept header
        headers.append('Accept', mediaTypesToAcceptString(await action.mediaTypes?.() ?? {}, maxAcceptHeaderLength));
        return headers;
    }
}
exports.ActorDereferenceHttpBase = ActorDereferenceHttpBase;
//# sourceMappingURL=ActorDereferenceHttpBase.js.map